import React, { useEffect, useMemo } from "react";
import {
  PageLayout,
  GreetingsWidget,
  ActivityLog,
  SetupChecklist,
} from "@components";
import { useWorkspace } from "../context/WorkspaceContext";
import { useNotifications } from "../context/NotificationContext";
import { useTranslation } from "react-i18next";
import { Card } from "flowbite-react";
import { OrderStatus } from "../types/order/IOrder";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { useNavigate } from "react-router-dom";
import { HiArrowRight } from "react-icons/hi";
import { useGlobalOrders } from "../hooks";

// Register necessary ChartJS components
ChartJS.register(ArcElement, Tooltip, Legend);

// Empty state component
interface EmptyStateProps {
  icon: React.ReactNode;
  message: string;
}

const EmptyState: React.FC<EmptyStateProps> = React.memo(
  ({ icon, message }) => (
    <div className="flex flex-col items-center justify-center h-full">
      {icon}
      <p className="text-muted">{message}</p>
    </div>
  ),
);

// Progress bar component with pulsing background
interface ProgressBarProps {
  percentage: number;
  hasOrders: boolean;
  completedText: string;
}

const ProgressBar: React.FC<ProgressBarProps> = React.memo(
  ({ percentage, hasOrders, completedText }) => (
    <div className="relative">
      <div
        className={`overflow-hidden h-6 text-xs flex rounded-full bg-brand-muted dark:bg-gray-700 relative`}
      >
        <div
          style={{
            width: `${percentage > 0 ? percentage : 3}%`,
            background: "linear-gradient(90deg, #7214FF 0%, #C7A1FF 100%)",
          }}
          className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center rounded-full transition-all duration-500 z-10 ${hasOrders ? "progress-bar-animated" : ""} ${percentage === 0 && hasOrders ? "progress-bar-pulse" : ""}`}
        >
          {percentage > 0 && (
            <span className="text-xs font-semibold z-10 px-2">
              {percentage}%{" "}
              <span className="hidden md:inline">{completedText}</span>
            </span>
          )}
        </div>
        {percentage === 0 && (
          <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 mx-auto my-auto">
            {percentage}%{" "}
            <span className="hidden md:inline">{completedText}</span>
          </span>
        )}
      </div>
    </div>
  ),
);

// Driver list component
// Removed DriverList component and related interfaces as they are no longer needed

// Icons for empty states
const ChartIcon = React.memo(() => (
  <svg
    className="w-12 h-12 text-muted mb-3"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    ></path>
  </svg>
));

// Removed DriverIcon as it's no longer needed

// Skeleton loader component
const SkeletonLoader: React.FC = React.memo(() => (
  <PageLayout>
    {/* Thin progress bar at the top */}
    <div className="mb-4">
      <div className="overflow-hidden h-6 text-xs flex rounded-full bg-surface-muted animate-pulse"></div>
    </div>

    {/* New layout for GreetingsWidget and KPI cards */}
    <div className="flex flex-col md:flex-row gap-4 mb-4">
      {/* GreetingsWidget skeleton (1/3 width) */}
      <div className="md:w-1/3 h-[220px] bg-surface-muted rounded-lg animate-pulse"></div>

      {/* KPI cards skeleton (2/3 width) */}
      <div className="md:w-2/3 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[...Array(4)].map((_, index) => (
          <Card key={index} className="dark:bg-[#0b0f26] sm:py-2 py-1">
            <div className="flex justify-between items-center">
              <div className="h-5 bg-surface-muted rounded w-24 animate-pulse"></div>
              <div className="h-8 bg-surface-muted rounded w-12 animate-pulse"></div>
            </div>
          </Card>
        ))}
      </div>
    </div>

    {/* KPI section skeleton */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
      {[...Array(2)].map((_, index) => (
        <Card key={index} className="dark:bg-[#0b0f26]">
          <div className="h-5 bg-surface-muted rounded w-32 mb-4 animate-pulse"></div>
          <div className="h-64 bg-surface-muted rounded animate-pulse"></div>
        </Card>
      ))}
    </div>
  </PageLayout>
));

// KPI card component
interface DashboardKpiCardProps {
  title: string;
  count: number;
  color: string;
  onClick?: () => void;
}

const DashboardKpiCard: React.FC<DashboardKpiCardProps> = React.memo(
  ({ title, count, color, onClick }) => (
    <Card
      className={`dark:bg-[#0b0f26] sm:py-2 py-1 h-full ${onClick ? "cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all duration-200" : ""}`}
      onClick={onClick}
    >
      <div className="flex justify-between items-center">
        <div className="flex items-center">
          <h5 className="text-sm sm:text-sm md:text-md font-bold tracking-tight text-text">
            {title}
          </h5>
          {onClick && (
            <HiArrowRight className="ml-1 sm:ml-2 text-muted opacity-70 transition-opacity hover:opacity-100 w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
          )}
        </div>
        <div
          className={`text-xl sm:text-2xl md:text-3xl font-bold ${count === 0 ? "text-muted" : color}`}
        >
          {count}
        </div>
      </div>
    </Card>
  ),
);

// CSS animation styles - defined outside the component to avoid re-creating on each render
const animationStyles = `
  @keyframes progressShimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: 0% 0;
    }
  }
  .progress-bar-animated {
    background-size: 200% 100% !important;
    background-image: linear-gradient(90deg, 
      #7214FF 0%, 
      #C7A1FF 45%, 
      #E9DEFF 50%, 
      #C7A1FF 55%, 
      #7214FF 100%) !important;
    animation-name: progressShimmer;
    animation-duration: 3s;
    animation-timing-function: ease-in-out;
    animation-delay: 7s;
    animation-iteration-count: infinite;
    animation-direction: normal;
    animation-fill-mode: none;
  }
  
  @media (prefers-color-scheme: dark) {
    .progress-bar-animated {
      background-image: linear-gradient(90deg, 
        #5A10CC 0%, 
        #9B7AD6 45%, 
        #C7A1FF 50%, 
        #9B7AD6 55%, 
        #5A10CC 100%) !important;
    }
  }
  
  .progress-bar-pulse {
    position: relative;
    overflow: hidden;
    animation: scale-pulse 4s ease-in-out infinite;
    transform-origin: center;
  }
  
  @keyframes scale-pulse {
    0% {
      transform: scaleY(0.95);
    }
    50% {
      transform: scaleY(1);
    }
    100% {
      transform: scaleY(0.95);
    }
  }
  
  .progress-bar-pulse::after {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
    animation: shimmer 3s infinite;
  }
  
  @keyframes shimmer {
    0% {
      left: -100%;
    }
    100% {
      left: 100%;
    }
  }
  
  @media (prefers-color-scheme: dark) {
    .progress-bar-pulse::after {
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.05), transparent);
    }
  }
`;

const RealtimeDashboard: React.FC = () => {
  const { activeWorkspace } = useWorkspace();
  const { t } = useTranslation(["common", "stats", "orders"]);
  const navigate = useNavigate();

  // Add CSS for animation only once when component mounts
  useEffect(() => {
    const styleEl = document.createElement("style");
    styleEl.innerHTML = animationStyles;
    document.head.appendChild(styleEl);

    return () => {
      document.head.removeChild(styleEl);
    };
  }, []);

  // State for dashboard data
  // const [totalOrderValue] = useState<number>(0);

  const workspaceId = activeWorkspace?.workspaceId ?? "";
  // Fetch data with the shared global listener - single realtime subscription
  // for cost optimization (finally matching the page name).
  const ordersQuery = useGlobalOrders();
  const {
    data: allOrders,
    isLoading: ordersLoading,
    isError: ordersError,
  } = ordersQuery;
  // Removed driversQuery as it's no longer needed

  // Get activity log from notifications
  const { activityLog } = useNotifications();

  // Calculate today's orders and status counts from global data
  const { todaysOrders, statusCounts, completionPercentage } = useMemo(() => {
    if (!allOrders)
      return {
        todaysOrders: [],
        statusCounts: { completed: 0, inProgress: 0, pending: 0, cancelled: 0 },
        completionPercentage: 0,
      };

    const today = new Date();
    const todayStr = today.toISOString().split("T")[0]; // YYYY-MM-DD format

    // Helper function to safely get date string
    const getDateString = (dateValue: any): string => {
      if (!dateValue) return "";

      try {
        // Handle Firestore Timestamp objects
        if (typeof dateValue.toDate === "function") {
          return dateValue.toDate().toISOString().split("T")[0];
        }

        // Handle Firestore Timestamp with seconds/nanoseconds
        if (dateValue.seconds) {
          const date = new Date(dateValue.seconds * 1000);
          return date.toISOString().split("T")[0];
        }

        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return "";

        return date.toISOString().split("T")[0];
      } catch (error) {
        return "";
      }
    };

    // Filter orders for today - using same date field as AllOrdersPage table
    const todaysOrdersFiltered = allOrders.filter((order) => {
      const orderDateStr = getDateString(order.createdAt);
      const routeDateStr =
        order.route && order.route.length > 0 && order.route[0].stopDate
          ? getDateString(order.route[0].stopDate)
          : "";
      const lastUpdatedStr = getDateString(order.lastUpdated);

      // Check if any date matches today (route[0].stopDate is primary - same as AllOrdersPage)
      return (
        orderDateStr === todayStr ||
        routeDateStr === todayStr ||
        lastUpdatedStr === todayStr
      );
    });

    // Calculate status counts
    const completed = todaysOrdersFiltered.filter(
      (order) => order.status === OrderStatus.Completed,
    ).length;
    const inProgress = todaysOrdersFiltered.filter(
      (order) => order.status === OrderStatus.InProgress,
    ).length;
    const pending = todaysOrdersFiltered.filter((order) =>
      [OrderStatus.Draft, OrderStatus.New, OrderStatus.Scheduled].includes(
        order.status,
      ),
    ).length;
    const cancelled = todaysOrdersFiltered.filter(
      (order) => order.status === OrderStatus.Cancelled,
    ).length;

    const statusCounts = { completed, inProgress, pending, cancelled };

    // Calculate completion percentage
    const total = todaysOrdersFiltered.length;
    const completionPercentage =
      total > 0 ? Math.round((completed / total) * 100) : 0;

    return {
      todaysOrders: todaysOrdersFiltered,
      statusCounts,
      completionPercentage,
    };
  }, [allOrders]);

  // Reference loading and error states
  const isLoading = ordersLoading;
  const isError = ordersError;

  // Prepare data for pie chart with memoization - only show relevant data
  const chartData = useMemo(() => {
    if (
      !statusCounts ||
      Object.values(statusCounts).every((count) => count === 0)
    ) {
      return null; // No data to show
    }

    // Filter out statuses with 0 count and use correct LGB colors
    const statusData = [
      {
        key: "completed",
        count: statusCounts.completed,
        label: t("stats:orderStatus.completed") || "Fullført",
        color: "#0AAF87", // lgb-green-300 for completed
        borderColor: "#0AAF87", // lgb-green-300 (same as fill for subtle border)
      },
      {
        key: "inProgress",
        count: statusCounts.inProgress,
        label: t("stats:orderStatus.in_progress") || "Pågående",
        color: "#7214FF", // lgb-blue-300 (lgb-primary) for in progress
        borderColor: "#7214FF", // lgb-blue-300 (same as fill for subtle border)
      },
      {
        key: "pending",
        count: statusCounts.pending,
        label: t("stats:pending") || "Ventende", // Use stats:pending instead of orderStatus.pending
        color: "#FCBD24", // lgb-yellow-300 for pending
        borderColor: "#FCBD24", // lgb-yellow-300 (same as fill for subtle border)
      },
      {
        key: "cancelled",
        count: statusCounts.cancelled,
        label: t("stats:orderStatus.cancelled") || "Kansellert",
        color: "#DE0028", // lgb-red-300 for cancelled
        borderColor: "#DE0028", // lgb-red-300 (same as fill for subtle border)
      },
    ].filter((status) => status.count > 0); // Only show categories with data

    if (statusData.length === 0) {
      return null; // No data to show
    }

    // Create canvas element for gradients (optional enhancement)
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    let backgroundColors = [];
    let borderColors = [];

    if (ctx) {
      // Create subtle gradients for each status using LGB colors
      backgroundColors = statusData.map((status) => {
        const gradient = ctx.createLinearGradient(0, 0, 200, 0);
        gradient.addColorStop(0, status.color);
        gradient.addColorStop(1, status.color + "80"); // Add transparency
        return gradient;
      });
      borderColors = statusData.map((status) => status.borderColor);
    } else {
      // Fallback to solid colors
      backgroundColors = statusData.map((status) => status.color);
      borderColors = statusData.map((status) => status.borderColor);
    }

    return {
      labels: statusData.map((status) => status.label),
      datasets: [
        {
          data: statusData.map((status) => status.count),
          backgroundColor: backgroundColors,
          borderColor: borderColors,
          borderWidth: 2,
        },
      ],
    };
  }, [statusCounts, t]);

  // Translations with memoization
  const translations = useMemo(
    () => ({
      completionProgress:
        t("stats:dashboard.completion_progress") || "Today's delivery progress",
      ordersCompleted:
        t("stats:dashboard.orders_completed") || "completed orders",
      orderStatusDistribution:
        t("stats:order_status_distribution") || "Order status distribution",
      totalOrderValue: t("stats:total_order_value") || "Total order value",
      forToday: t("stats:for_today") || "for today",
      noOrdersToday: t("stats:dashboard.no_data") || "No orders today",
      inProgress:
        t("stats:orderStatus.in_progress") ||
        t("stats:orders.in_progress") ||
        "In progress",
      pending: t("stats:pending") || t("stats:orders.pending") || "Pending",
      completed:
        t("stats:orderStatus.completed") ||
        t("stats:order_status.completed") ||
        "Completed",
      cancelled:
        t("stats:orderStatus.cancelled") ||
        t("stats:order_status.cancelled") ||
        "Cancelled",
    }),
    [t],
  );

  // Calculate order counts by status
  const orderStatusCounts = useMemo(() => {
    const inProgressCount = statusCounts.inProgress;
    const pendingCount = statusCounts.pending;
    const completedCount = statusCounts.completed;
    const cancelledCount = statusCounts.cancelled;

    return {
      inProgressCount,
      pendingCount,
      completedCount,
      cancelledCount,
    };
  }, [statusCounts]);

  // Show skeleton loader if data is loading
  if (isLoading && !isError) {
    return <SkeletonLoader />;
  }

  // Show error message if there was an error fetching data
  if (isError) {
    return (
      <PageLayout>
        <div className="p-4 bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-100 rounded-lg">
          <h3 className="text-lg font-semibold mb-2">
            Error loading dashboard data
          </h3>
          <p>
            There was a problem loading order data. Please try refreshing the
            page or contact support if the problem persists.
          </p>
        </div>
      </PageLayout>
    );
  }

  // Show loading message if no workspace is available
  if (!workspaceId) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">Loading workspace...</p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      {/* In-app onboarding guidance for freshly set-up workspaces */}
      <SetupChecklist />

      {/* Thin progress bar at the top - only shown when there are orders */}
      {todaysOrders && todaysOrders.length > 0 && (
        <div className="mb-4">
          <ProgressBar
            percentage={completionPercentage}
            hasOrders={todaysOrders.length > 0}
            completedText={translations.ordersCompleted}
          />
        </div>
      )}

      {/* New layout for GreetingsWidget and KPI cards */}
      <div className="flex flex-col md:flex-row gap-4 mb-4">
        {/* GreetingsWidget (1/3 width) */}
        <div className="md:w-1/3">
          <GreetingsWidget />
        </div>

        {/* Dashboard KPI cards (2/3 width) - grid on desktop, column on mobile */}
        <div className="md:w-2/3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
            <DashboardKpiCard
              title={translations.inProgress}
              count={statusCounts.inProgress}
              color="text-indigo-600"
              onClick={() => navigate("/orders?status=InProgress")}
            />
            <DashboardKpiCard
              title={translations.pending}
              count={statusCounts.pending}
              color="text-yellow-500"
              onClick={() => navigate("/orders?status=New,Scheduled")}
            />
            <DashboardKpiCard
              title={translations.completed}
              count={statusCounts.completed}
              color="text-green-500"
              onClick={() => navigate("/orders?status=Completed")}
            />
            <DashboardKpiCard
              title={translations.cancelled}
              count={statusCounts.cancelled}
              color="text-red-500"
              onClick={() => navigate("/orders?status=Cancelled")}
            />
          </div>
        </div>
      </div>

      {/* KPI section - Pie Chart and Activity Log */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 items-start">
        <Card className="dark:bg-[#0b0f26] h-full">
          <h5 className="text-lg font-bold tracking-tight text-text">
            {translations.orderStatusDistribution}
          </h5>
          <div className="h-80">
            {chartData ? (
              <Pie
                data={chartData}
                options={{
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      labels: {
                        color: "#6B7280",
                      },
                    },
                  },
                }}
              />
            ) : (
              <EmptyState
                icon={<ChartIcon />}
                message={translations.noOrdersToday}
              />
            )}
          </div>
        </Card>

        {/* Activity Log section */}
        <Card className="dark:bg-[#0b0f26] h-full">
          <div className="flex items-center justify-between mb-4">
            <h5 className="text-lg font-bold tracking-tight text-text">
              Aktivitetslogg
            </h5>
            <span className="text-sm text-muted">
              Siste 10 hendelser
            </span>
          </div>
          <div className="h-80 overflow-y-auto">
            <ActivityLog activities={activityLog} maxItems={10} />
          </div>
        </Card>
      </div>
    </PageLayout>
  );
};

export default RealtimeDashboard;
